import { z } from "zod";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { TopicPartQuestionFormData } from "../../topicPartQuestion/entities/TopicPartQuestion";
import { commentValidation } from "../../../utils/validation";
import { TranslationFunction } from "../../../utils/utils";

export const useTopicPartQuestionFormValidation = (
  data: TopicPartQuestionFormData | null,
  t: TranslationFunction
): UseFormReturn<TopicPartQuestionFormData> => {
  const schema = z
    .object({
      topicPartId: z.union([z.string(), z.number()]),
      text: z
        .string()
        .nonempty({ message: t("validation.required") })

        .min(5, { message: t("validation.requiredMin", { count: 5 }) })
        .max(200, { message: t("validation.requiredMax", { count: 200 }) })
        .refine((val) => !val.includes("\n"), {
          message: t("validation.noNewLines"),
        }),
      answerType: z.enum(
        ["text", "singleChoice", "multipleChoice", "yesNo", "yesNoUnknown"],
        {
          errorMap: () => ({ message: t("validation.required") }),
        }
      ),
      answers: z.array(z.string().nonempty()).optional().nullable(),
      comments: commentValidation(),
    })
    .refine(
      (data) =>
        !["multipleChoice", "singleChoice"].includes(data.answerType) ||
        (data.answers && data.answers.length >= 2),
      {
        message: t("validation.arrayMin", { count: 2 }),
        path: ["answers"],
      }
    )
    .transform((data) => ({
      ...data,
      answers: ["multipleChoice", "singleChoice"].includes(data.answerType)
        ? data.answers
        : [],
    }));

  const form = useForm<TopicPartQuestionFormData>({
    resolver: zodResolver(schema),
    defaultValues: data || {},
  });

  useEffect(() => {
    form.reset({ ...(data ?? {}) });
  }, [data, form]);

  return form;
};
